import { DateTime } from "luxon";
import { OrderStatusEnum } from "@src/features/order/entities";
import { DATE_PRESET_LABEL_KEYS } from "./constants";
import { DatePreset, OrderFilters } from "./types";

export interface ResolvedDateRange {
  dateFrom?: string;
  dateTo?: string;
}

export type Translate = (key: string, options?: Record<string, unknown>) => string;

const fromInstant = (day: DateTime): string | undefined =>
  day.startOf("day").toUTC().toISO({ suppressMilliseconds: true }) ?? undefined;

const toInstant = (day: DateTime): string | undefined =>
  day.endOf("day").toUTC().toISO({ suppressMilliseconds: true }) ?? undefined;

const range = (start: DateTime, end: DateTime): ResolvedDateRange => ({
  dateFrom: fromInstant(start),
  dateTo: toInstant(end),
});

export const resolveDateRange = (filters: OrderFilters): ResolvedDateRange => {
  const now = DateTime.now();

  switch (filters.datePreset) {
  case DatePreset.TODAY:
    return range(now, now);
  case DatePreset.YESTERDAY: {
    const yesterday = now.minus({ days: 1 });
    return range(yesterday, yesterday);
  }
  case DatePreset.LAST_7_DAYS:
    return range(now.minus({ days: 6 }), now);
  case DatePreset.LAST_30_DAYS:
    return range(now.minus({ days: 29 }), now);
  case DatePreset.THIS_MONTH:
    return range(now.startOf("month"), now.endOf("month"));
  case DatePreset.CUSTOM: {
    if (!filters.customStart || !filters.customEnd) return {};
    return range(DateTime.fromISO(filters.customStart), DateTime.fromISO(filters.customEnd));
  }
  default:
    return {};
  }
};

export const isCustomRangeIncomplete = (filters: OrderFilters): boolean =>
  filters.datePreset === DatePreset.CUSTOM && (!filters.customStart || !filters.customEnd);

export const formatFilterDay = (value: string): string =>
  DateTime.fromISO(value).toFormat("d.MM.yyyy");

export const getDateFilterLabel = (filters: OrderFilters, t: Translate): string => {
  if (filters.datePreset !== DatePreset.CUSTOM) {
    return t(DATE_PRESET_LABEL_KEYS[filters.datePreset]);
  }
  if (filters.customStart && filters.customEnd) {
    return `${formatFilterDay(filters.customStart)} - ${formatFilterDay(filters.customEnd)}`;
  }
  return t(DATE_PRESET_LABEL_KEYS[DatePreset.CUSTOM]);
};

export const getOrderTypeLabel = (status: OrderStatusEnum, t: Translate): string =>
  t(`history.filter.orderType.${status}`);

export const countAppliedFilters = (filters: OrderFilters): number => {
  let count = 0;
  if (filters.orderTypes.length > 0) count += 1;
  if (filters.hubIds.length > 0) count += 1;
  if (filters.datePreset !== DatePreset.ALL_TIME) count += 1;
  return count;
};
